import React from 'react';
import styled from 'styled-components';
import { Check, ArrowRight } from 'lucide-react';
import { breakpoints } from '@/layouts/breakpoints';
import { Link } from 'react-router-dom'
import LazyVideo from '@/components/landing/common/LazyVideo'

const StyledComp = styled.div`
  background: var(--white);
  color: var(--black);
  min-height: 900px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  position: relative;
  overflow: hidden;
  @media (max-width: 1023px) {
    min-height: 0;
    padding: 0 24px;
  }

  @media (min-width: 768px) {
    padding: 0 40px;
  }

  .container {
    display: flex;
    flex-direction: column;
    gap: 48px;
    align-items: center;
    width: 100%;
    max-width: 1400px;

    @media (max-width: 1023px) {
      position: relative;
      z-index: 1;
    }

    @media (min-width: 1024px) {
      flex-direction: row;
      gap: 80px;
    }

    @media (min-width: 1200px) {
      gap: 120px;
    }

    @media (min-width: 1400px) {
      gap: 200px;
    }
  }

  .left-section {
    width: 100%;

    @media (min-width: 1024px) {
      flex: 1;
    }

    @media (max-width: 1023px) {
      position: absolute;
      inset: 0;
      z-index: 0;
    }
  }

  .card {
    background: #1a1a1a;
    border-radius: 20px;
    position: relative;
    overflow: hidden;
    width: 100%;
    aspect-ratio: 9/16;
    max-height: 700px;
    margin: 0 auto;

    @media (max-width: 1023px) {
      max-height: none;
      aspect-ratio: auto;
      height: 100%;
      border-radius: 0;
    }

    .video-container, video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .right-section {
    width: 100%;

    @media (min-width: 1024px) {
      flex: 1;
    }

    @media (max-width: 1023px) {
      background: rgba(255, 255, 255, 0.9);
      backdrop-filter: blur(12px);
      padding: 80px 32px;
      z-index: 2;
    }
  }

  .heading {
    font-size: 32px;
    font-weight: 600;
    line-height: 1.2;
    margin-bottom: 32px;

    @media (min-width: 480px) {
      font-size: 36px;
    }

    @media (min-width: 768px) {
      font-size: 42px;
      margin-bottom: 40px;
    }

    @media (min-width: 1024px) {
      font-size: 44px;
    }

    @media (min-width: 1200px) {
      font-size: 48px;
      margin-bottom: 50px;
    }
  }

  .features {
    display: flex;
    flex-direction: column;
    gap: 28px;

    @media (min-width: 768px) {
      gap: 32px;
    }

    @media (min-width: 1024px) {
      gap: 36px;
    }

    @media (min-width: 1200px) {
      gap: 40px;
    }
  }

  .feature-item {
    display: flex;
    gap: 16px;

    @media (min-width: 768px) {
      gap: 20px;
    }
  }

  .check-icon {
    width: 22px;
    height: 22px;
    background: #2a2a2a;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;

    @media (min-width: 768px) {
      width: 24px;
      height: 24px;
      margin-top: 4px;
    }

    svg {
      width: 12px;
      height: 12px;
      color: #fff;

      @media (min-width: 768px) {
        width: 14px;
        height: 14px;
      }
    }
  }

  .feature-content {
    flex: 1;
  }

  .feature-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
    line-height: 1.3;

    @media (min-width: 768px) {
      font-size: 17px;
      margin-bottom: 10px;
    }

    @media (min-width: 1024px) {
      font-size: 18px;
      margin-bottom: 12px;
    }
  }

  .feature-description {
    font-size: 14px;
    color: #888;
    line-height: 1.5;
    margin-bottom: 10px;

    @media (min-width: 768px) {
      font-size: 15px;
      line-height: 1.6;
      margin-bottom: 12px;
    }
  }

  .feature-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--black);
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    transition: gap 0.2s;
    padding: 4px 0; /* Larger touch target */

    @media (min-width: 768px) {
      font-size: 15px;
      gap: 8px;
    }

    &:hover {
      gap: 10px;

      @media (min-width: 768px) {
        gap: 12px;
      }
    }

    svg {
      width: 14px;
      height: 14px;

      @media (min-width: 768px) {
        width: 16px;
        height: 16px;
      }
    }
  }
`;

const Comp = ({ title = '', points = [], vid = '' }) => {
  return (
    <StyledComp>
      <div className="container">
        <div className="left-section">
          <div className="card">

            <LazyVideo
              classNames={`video fade-in`}
              src={vid}
            />
          </div>
        </div>

        <div className="right-section">
          <h2 className="heading serif">{title}</h2>

          <div className="features">
            {points.map((point, index) => (
              <div className="feature-item" key={index}>
                <div className="check-icon">
                  <Check strokeWidth={3} />
                </div>
                <div className="feature-content">
                  <div className="feature-title">{point.title}</div>
                  <div className="feature-description">{point.details}</div>
                  <Link to={point.url || "/dashboard"} className="feature-link">
                    {point.cta} <ArrowRight />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </StyledComp>
  );
};

export default Comp;
